import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { MessageSquare, X, SendHorizonal, Loader2 } from 'lucide-react';
import { useLang } from '../i18n/LangContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatAssistant() {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    setMessages([{ role: 'assistant', content: t('chat.welcome') }]);
    setSessionId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, sending, open]);

  const sendMessage = async (text) => {
    const content = (text ?? input).trim();
    if (!content || sending) return;

    const next = [...messages, { role: 'user', content }];
    setMessages(next);
    setInput('');
    setSending(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        language: lang,
        messages: next,
      });
      setSessionId(res.data.session_id);
      setMessages([...next, { role: 'assistant', content: res.data.reply }]);
    } catch (err) {
      const fallback = lang === 'es'
        ? 'Disculpe, hay un problema temporal con el asistente. Por favor escriba a direccion@alpha7.com.mx.'
        : 'Sorry — the assistant is temporarily unavailable. Please write to direccion@alpha7.com.mx.';
      setMessages([...next, { role: 'assistant', content: fallback }]);
    } finally {
      setSending(false);
    }
  };

  const quickActions = t('chat.quick');

  return (
    <>
      {!open && (
        <button
          data-testid="chat-toggle-button"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 md:bottom-7 md:right-7 z-40 inline-flex items-center gap-3 bg-[#C8102E] hover:bg-[#0A0A0B] hover:border-[#C8102E] text-white px-5 py-3 border border-[#C8102E] transition-colors shadow-[0_8px_30px_rgba(200,16,46,0.35)]"
        >
          <MessageSquare size={16} />
          <span className="font-mono text-[11px] tracking-[0.22em] uppercase">{t('chat.open')}</span>
        </button>
      )}

      {open && (
        <div
          data-testid="chat-panel"
          className="chat-panel fixed bottom-5 right-5 md:bottom-7 md:right-7 z-40 w-[min(380px,calc(100vw-2.5rem))] h-[min(560px,calc(100vh-3rem))] flex flex-col"
        >
          <div className="flex items-center justify-between p-4 border-b border-[#27272A] bg-[#0A0A0B]">
            <div className="flex items-center gap-3">
              <span className="w-2 h-2 bg-[#C8102E]" />
              <div>
                <div className="font-display text-base text-[#F5F5F2] leading-none">{t('chat.title')}</div>
                <div className="mt-1 font-mono text-[9px] tracking-[0.24em] uppercase text-[#A1A1AA]">{t('chat.subtitle')}</div>
              </div>
            </div>
            <button data-testid="chat-close-button" aria-label="Close chat" onClick={() => setOpen(false)} className="w-8 h-8 inline-flex items-center justify-center border border-[#27272A] hover:border-[#C8102E] hover:text-[#C8102E] transition-colors">
              <X size={14} />
            </button>
          </div>

          <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === 'user' ? 'chat-bubble-user' : 'chat-bubble-bot'}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="chat-bubble-bot px-4 py-2.5 text-sm inline-flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" />
                  <span className="text-[#A1A1AA]">…</span>
                </div>
              </div>
            )}
          </div>

          {/* Quick actions */}
          <div className="px-4 pb-2 flex flex-wrap gap-2">
            {quickActions.slice(0, 4).map((q) => (
              <button
                key={q}
                data-testid={`chat-quick-${q.replace(/\s+/g, '-').toLowerCase()}`}
                onClick={() => sendMessage(q)}
                disabled={sending}
                className="text-[10px] font-mono tracking-[0.18em] uppercase px-3 py-1.5 border border-[#27272A] text-[#A1A1AA] hover:border-[#C8102E] hover:text-[#C8102E] transition-colors disabled:opacity-50"
              >
                {q}
              </button>
            ))}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
            className="border-t border-[#27272A] p-3 flex items-center gap-2 bg-[#0A0A0B]"
          >
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('chat.placeholder')}
              className="flex-1 bg-transparent text-sm text-[#F5F5F2] outline-none placeholder:text-[#52525B] px-2 py-2"
            />
            <button
              data-testid="chat-send-button"
              type="submit"
              disabled={sending || !input.trim()}
              className="w-9 h-9 inline-flex items-center justify-center border border-[#27272A] hover:border-[#C8102E] hover:text-[#C8102E] transition-colors disabled:opacity-50"
            >
              <SendHorizonal size={14} />
            </button>
          </form>

          <div className="px-4 pb-3 text-[10px] text-[#52525B] leading-relaxed">{t('chat.disclaimer')}</div>
        </div>
      )}
    </>
  );
}
